// Chakra Imports
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Flex,
  Link,
  useColorModeValue,
  FlexProps,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { useMediaQuery } from "@chakra-ui/react";
import AdminNavbarLinks from "./AdminNavbarLinks";

interface AdminNavbarProps extends FlexProps {
  variant?: string;
  children?: React.ReactNode;
  fixed?: boolean;
  secondary?: boolean;
  brandText?: string;
  logoText?: string;
  onOpen?: () => void;
}

export default function AdminNavbar(props: AdminNavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [isMobile] = useMediaQuery("(max-width: 1023px)");
  const {
    variant,
    children,
    fixed,
    secondary,
    brandText,
    onOpen,
    ...rest
  } = props;

  // Here are all the props that may change depending on navbar's type or state.(secondary, variant, scrolled)
  let mainText = useColorModeValue("gray.700", "gray.200");
  let secondaryText = useColorModeValue("gray.400", "gray.200");
  let navbarPosition = "absolute";
  let navbarFilter = "none";
  // Remove glass effect on mobile/tablet breakpoints (only show on xl and above)
  let navbarBackdrop = isMobile ? "none" : "blur(21px)";
  let navbarShadow = "none";
  // Remove background and border on mobile/tablet to match mobile styling
  let navbarBg = isMobile ? "transparent" : "none";
  let navbarBorder = isMobile ? "transparent" : "transparent";
  let secondaryMargin = "0px";
  let paddingX = "15px";
  if (props.fixed === true)
    if (scrolled === true) {
      navbarPosition = "fixed";
      navbarShadow = useColorModeValue(
        "0px 7px 23px rgba(0, 0, 0, 0.05)",
        "none"
      );
      navbarBg = useColorModeValue(
        "linear-gradient(112.83deg, rgba(255, 255, 255, 0.82) 0%, rgba(255, 255, 255, 0.8) 110.84%)",
        "linear-gradient(112.83deg, rgba(255, 255, 255, 0.21) 0%, rgba(255, 255, 255, 0) 110.84%)"
      );
      navbarBorder = useColorModeValue("#FFFFFF", "rgba(255, 255, 255, 0.31)");
      navbarFilter = useColorModeValue(
        "none",
        "drop-shadow(0px 7px 23px rgba(0, 0, 0, 0.05))"
      );
    }
  if (props.secondary) {
    navbarBackdrop = "none";
    navbarPosition = "absolute";
    mainText = "white";
    secondaryText = "white";
    secondaryMargin = "22px";
    paddingX = "30px";
  }
  const changeNavbar = () => {
    if (window.scrollY > 1) {
      setScrolled(true);
    } else {
      setScrolled(false);
    }
  };
  
  useEffect(() => {
    window.addEventListener("scroll", changeNavbar);
    return () => {
      window.removeEventListener("scroll", changeNavbar);
    };
  }, []);

  return (
    <Flex
      position={navbarPosition as any}
      boxShadow={navbarShadow}
      bg={navbarBg}
      borderColor={navbarBorder}
      filter={navbarFilter}
      backdropFilter={navbarBackdrop}
      borderWidth={isMobile ? "0px" : "1.5px"}
      borderStyle="solid"
      transitionDelay="0s, 0s, 0s, 0s"
      transitionDuration=" 0.25s, 0.25s, 0.25s, 0s"
      transitionProperty="box-shadow, background-color, filter, border"
      transitionTimingFunction="linear, linear, linear, linear"
      alignItems={{ xl: "center" }}
      borderRadius={isMobile ? "0px" : "16px"}
      display="flex"
      minH="75px"
      justifyContent={{ xl: "center" }}
      lineHeight="25.6px"
      mx="auto"
      mt={secondaryMargin}
      pb="8px"
      left={document.documentElement.dir === "rtl" ? { sm: "0px", md: "0px", lg: "0px", xl: "30px" } : { sm: "0px", md: "0px", lg: "0px", xl: "" }}
      right={document.documentElement.dir === "rtl" ? { sm: "0px", md: "0px", lg: "0px", xl: "" } : { sm: "0px", md: "0px", lg: "0px", xl: "30px" }}
      px={{
        sm: "30px",
        md: "30px",
        lg: "30px",
      }}
      pe={{
        sm: "30px",
        md: "30px",
        lg: "30px",
      }}
      ps={{
        xl: "12px",
      }}
      pt="8px"
      top={{ sm: "45px", md: "45px", lg: "45px", xl: "18px" }}
      w={{ sm: "100vw", md: "100vw", lg: "100vw", xl: "calc(100vw - 75px - 275px)" }}
      zIndex={1000}
      {...rest}
    >
      <Flex
        w="100%"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box flexShrink={0} display="none">
          <Breadcrumb>
            <BreadcrumbItem color={mainText}>
              <BreadcrumbLink href="#" color={secondaryText}>
                Pages
              </BreadcrumbLink>
            </BreadcrumbItem>

            <BreadcrumbItem color={mainText}>
              <BreadcrumbLink href="#" color="#38BBD1">
                {brandText}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>
          {/* Here we create navbar brand, based on route name */}
          <Link
            color="#38BBD1"
            href="#"
            bg="inherit"
            borderRadius="inherit"
            fontWeight="bold"
            _hover={{ color: "#38BBD1" }}
            _active={{
              bg: "inherit",
              transform: "none",
              borderColor: "transparent",
            }}
            _focus={{
              boxShadow: "none",
            }}
          >
            {brandText}
          </Link>
        </Box>
        <Box 
          flexShrink={0} 
          ms={{ sm: "0", xl: "auto" }}
          w={{ sm: "100%", xl: "auto" }}
        >
          <AdminNavbarLinks
            onOpen={props.onOpen}
            logoText={props.logoText}
            secondary={props.secondary}
            fixed={props.fixed}
          />
        </Box>
      </Flex>
    </Flex>
  );
}

